/**
 * 这里统一管理所有首页弹窗组件
 * 弹窗显示顺序
 * 1. 问卷弹窗 - 已下线
 * 2. 活动弹窗
 */

import React, { useState, useEffect } from 'react';
import ActivityPopup, { type ActivityPopupMediaType } from '@/components/ActivityPopup';
import { PopupInfoResponse, type PopupMediaAsset } from '$/api/server.types';
import { getPopupInfo } from '$/api/canvasRquest';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from '$/utils/mtstat';
import { isPagUrl } from '@/utils';

enum PopupName {
  Activity = 'ActivityPopup',
  Survey = 'SurveyPopup',
}

interface PopupInfo {
  type: PopupName;
  image: string;
  fallbackImage?: string;
  url: string;
  mediaType: ActivityPopupMediaType;
  width?: number;
  height?: number;
  pagPlayCount?: number;
  id?: string | number;
  name?: string;
  closeBtnPosition?: 'topRight' | 'bottomCenter';
}

const closeBtnPositionMap: Record<string, 'topRight' | 'bottomCenter'> = {
  right_top: 'topRight',
  middle_bottom: 'bottomCenter',
};
const HOME_POPUP_POSITION = 'home';

/**
 * 读取媒体资源宽高；非法 / 缺失时返回 undefined，交由展示层默认值兜底。
 */
function pickPopupMediaSize(asset?: PopupMediaAsset) {
  const { width, height } = asset || {};
  return {
    width: width && width > 0 ? width : undefined,
    height: height && height > 0 ? height : undefined,
  };
}

/**
 * 为 PAG 首帧和失败兜底选择静态素材；img 自身是 PAG 时不能当作 img 标签兜底。
 */
function pickStaticFallbackAsset(img?: PopupMediaAsset, fallbackImg?: PopupMediaAsset) {
  if (fallbackImg?.url) {
    return fallbackImg;
  }
  if (isPagUrl(img?.url || '')) {
    return undefined;
  }
  return img;
}

/**
 * 解析 PAG 播放次数；后端未下发或下发 -1 时保持无限循环。
 */
function resolvePagPlayCount(pag?: PopupMediaAsset) {
  if (pag?.play_count === undefined || pag.play_count === -1) {
    return undefined;
  }
  return pag.play_count;
}

/**
 * 从弹窗 ext 中解析主素材、首帧静态图与失败兜底图，展示优先级为 pag > fallback_img > img。
 */
function resolveActivityPopupMedia(ext: PopupInfoResponse['popup'][number]['ext']) {
  const { pag, img, fallback_img: fallbackImg } = ext;
  const imgUrl = img?.url || '';
  const hasPag = Boolean(pag?.url) || isPagUrl(imgUrl);
  const staticFallback = pickStaticFallbackAsset(img, fallbackImg);

  if (hasPag) {
    const primary = pag?.url ? pag : img;
    const { width, height } = pickPopupMediaSize(primary);
    return {
      image: primary?.url || '',
      mediaType: 'pag' as const,
      fallbackImage: staticFallback?.url || undefined,
      width,
      height,
      pagPlayCount: resolvePagPlayCount(pag),
    };
  }

  const primary = staticFallback;
  const { width, height } = pickPopupMediaSize(primary);
  return {
    image: primary?.url || '',
    mediaType: 'image' as const,
    fallbackImage: undefined,
    width,
    height,
  };
}

export default function HomePopups() {
  const userInfo = useOfficialStore((state) => state.userInfo);

  const [open, setOpen] = useState(false);
  const [popupList, setPopupList] = useState<PopupInfo[]>([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  const currentPopup = popupList[currentPopupIndex] || {};

  const handlePopupClose = () => {
    // 如果是问卷弹窗，缓存用户id,下次不再显示
    if (currentPopup.type === PopupName.Survey) {
      localStorageUtil.setItem('surverPopupUid', userInfo.uid);
    }
    if (currentPopupIndex < popupList.length - 1) {
      setCurrentPopupIndex(currentPopupIndex + 1);
    } else {
      setOpen(false);
    }
  };

  // 关闭活动弹窗
  const handleActivityPopupClose = () => {
    if (currentPopup.type === PopupName.Activity) {
      mtstatTrack('home_banner_click', {
        id: currentPopup.id,
        name: currentPopup.name,
        click_type: 'close',
      });
    }

    handlePopupClose();
  };

  // PAG 素材加载失败且无兜底图时，跳过当前弹窗并继续后续队列。
  const handleActivityMediaError = () => {
    handlePopupClose();
  };

  // 弹窗图片点击
  const handleActivityPopupImageClick = () => {
    if (currentPopup.type === PopupName.Activity) {
      mtstatTrack('home_banner_click', {
        id: currentPopup.id,
        name: currentPopup.name,
        click_type: 'click',
      });
    }
    if (currentPopup.url) {
      window.open(currentPopup.url, '_blank');
    }
    handlePopupClose();
  };

  // 是否显示活动弹窗
  const checkShowActivityPopup = (data: PopupInfoResponse) => {
    return data.popup.some((item) => item.position === HOME_POPUP_POSITION);
  };

  /**
   * 请求首页活动弹窗配置。
   */
  async function requestPopupInfo() {
    return getPopupInfo({ position: 'home' });
  }

  /**
   * 拉取弹窗配置并组装待展示队列（优先 PAG，失败由组件内 fallback_img 兜底）。
   */
  async function getPopupsInfo() {
    const res = await Promise.all([
      requestPopupInfo(),
    ]);
    const [popupInfo] = res;

    // 是否显示活动弹窗
    const showActivityPopup = checkShowActivityPopup(popupInfo);
    // 构造要显示的弹窗列表
    const newPopupList: PopupInfo[] = [];

    if (showActivityPopup) {
      popupInfo.popup
        .filter((item) => item.position === HOME_POPUP_POSITION)
        .forEach((item) => {
          const media = resolveActivityPopupMedia(item.ext);
          // 无有效素材地址时跳过，避免空弹窗
          if (!media.image) return;
          newPopupList.push({
            id: item.id,
            name: item.title,
            type: PopupName.Activity,
            image: media.image,
            fallbackImage: media.fallbackImage,
            url: item.ext.scheme,
            mediaType: media.mediaType,
            width: media.width,
            height: media.height,
            pagPlayCount: media.pagPlayCount,
            closeBtnPosition: closeBtnPositionMap[item.ext.close_btn.position],
          });
        });
    }

    if (newPopupList.length > 0) {
      setCurrentPopupIndex(0);
      setPopupList(newPopupList);
      setOpen(true);
    }
  }

  useEffect(() => {
    if (currentPopup.type === PopupName.Activity && open) {
      mtstatTrack('home_banner_exp', {
        id: currentPopup.id,
        name: currentPopup.name,
      });
    }
  }, [currentPopup.id, open]);

  useEffect(() => {
    if (userInfo.uid) {
      getPopupsInfo();
    }
  }, [userInfo.uid]);

  return (
    <div>
      <ActivityPopup
        open={open}
        image={currentPopup.image}
        fallbackImage={currentPopup.fallbackImage}
        mediaType={currentPopup.mediaType}
        width={currentPopup.width}
        height={currentPopup.height}
        pagPlayCount={currentPopup.pagPlayCount}
        closeBtnPosition={currentPopup.closeBtnPosition}
        onClose={handleActivityPopupClose}
        onImageClick={handleActivityPopupImageClick}
        onMediaError={handleActivityMediaError}
      />
    </div>
  );
}
